import React, { useState, useEffect, useRef } from 'react'
import { useLocation, useHistory } from 'react-router-dom'
import moment from 'moment'
import { MainHeading, Divider, DatePicker, SpinnerDotted, Spinner, Breadcrumb, DSIcon } from '../../components/common/atoms'
import { Table, Pagination } from '../../components/common/molecules'
import { handleAppError } from '../../components/handlers'
import { RaiseDisputeModal } from '../../components/modals'
import { successOrFailureToaster } from '../../components/common/atoms/messages'
import { PARTICIPATION_BREAK_UP_HEADERS, PARTICIPATION_LIST_HEADERS } from '../../constants/tableHeaders'
import { invoiceBreakUpSchema, misParticipantsListSchema } from '../../schemas/table'
import { popup_close as closebtn, white_tick_icon, x_red, exclametry_red_dark } from '../../assets/images'
import InvoicesApi from '../../services/invoices.api'
import { SALES_ROLE } from '../../constants/overview'
import { dsButtonPrimary, dsButtonSecondary, dsButtonGhostBrand, joinButtonClasses } from '../../utils/buttonStyles'

const getBreakUpHeaders = (service, gst_percentage) => {
  service = (service || '').trim().toLowerCase()

  let headers = []
  if (['health checks', 'vaccination', 'vision', 'dental', 'onsite', 'engagement activity', 'ohc'].includes(service)) headers = PARTICIPATION_BREAK_UP_HEADERS['ppu_headers'](service, gst_percentage)
  else if (['doctor consultation', 'opd', 'ewap', 'smart cover'].includes(service)) headers = PARTICIPATION_BREAK_UP_HEADERS['subscription_headers'](gst_percentage)
  else {
    const callback = PARTICIPATION_BREAK_UP_HEADERS[service.replace(/ /g, '_')]
    headers = callback ? callback(gst_percentage) : []
  }

  return headers
}

const getParticipationListHeaders = (service) => {
  service = (service || '').trim().toLowerCase()

  let headers = []
  if (['health checks', 'vaccination', 'vision', 'dental', 'onsite', 'engagement activity', 'ohc'].includes(service)) headers = PARTICIPATION_LIST_HEADERS['ppu_headers']
  else if (['doctor consultation', 'opd', 'ewap', 'smart cover', 'gym', 'employee addition', 'health coach'].includes(service)) headers = PARTICIPATION_LIST_HEADERS['subscription_headers']

  return headers
}

const MISInvoices = () => {
  const location = useLocation()
  const history = useHistory()
  const pathState = location && location.state
  const [data, setData] = useState({ table_loading: true, summary_loading: true })
  const [paginationData, setPaginationData] = useState({ total_pages: 0, cur_page: 0, total_results: 0 })
  const [flags, setFlags] = useState({ disputeModal: false, spinner: false })
  const dates = useRef({ start_date: pathState.start_date || moment().startOf('month').format('DD-MM-YYYY'), end_date: pathState.end_date || moment().endOf('month').format('DD-MM-YYYY') })

  const fetchData = () => {
    setData(prevState => { return { ...prevState, table_loading: true, summary_loading: true } })
    const data = { company_invoice_id: pathState.invoice_id, company_invoice_item_id: pathState.invoice_item_id, page: 0 }
    const promise1 = InvoicesApi.fetch_mis_summary({ data })
    const promise2 = InvoicesApi.fetch_participants_list({ data })

    Promise.all([promise1, promise2])
      .then((values) => {
        const resp_arr = values.map((resp) => resp.data)
        const total_results = (resp_arr[1].meta && resp_arr[1].meta.total) || 0
        setPaginationData({ total_pages: Math.ceil(total_results / 10), cur_page: 0, total_results })
        setData({ ...resp_arr[0], ...resp_arr[1], table_loading: false, summary_loading: false })
      })
      .catch((err) => {
        handleAppError(err)
        setData(prevState => { return { ...prevState, table_loading: false, summary_loading: false } })
      })
  }

  const navigateToPage = (page) => {
    setData(prevState => { return { ...prevState, table_loading: true } })
    InvoicesApi.fetch_participants_list({ data: { company_invoice_id: pathState.invoice_id, company_invoice_item_id: pathState.invoice_item_id, page } },
      (res) => {
        const data = res.data || {}
        setPaginationData(prevState => { return { ...prevState, cur_page: page } })
        setData(prevState => { return { ...prevState, ...data, table_loading: false } })
      },
      (err) => {
        handleAppError(err)
        setData(prevState => { return { ...prevState, table_loading: false } })
      }
    )
  }

  const disputeHandler = (type, comment) => {
    if (['close', 'open'].includes(type)) {
      setFlags(prevState => { return { ...prevState, disputeModal: !prevState.disputeModal } })
    } else if (type === 'submit') {
      setFlags({ spinner: true, disputeModal: false })
      const data = { company_invoice_id: pathState.invoice_id, company_invoice_item_id: pathState.invoice_item_id, comment }
      InvoicesApi.dispute_invoice({ data },
        (_) => {
          setFlags({ spinner: false, disputeModal: false })
          fetchData()
          successOrFailureToaster({ message: 'Invoice has been rejected', type: 'custom', customColor: '#C80010', customIcon: exclametry_red_dark })
        },
        (err) => {
          const message = (err.response && err.response.data && err.message) || 'Error occurred while raising dispute'
          setFlags({ spinner: false, disputeModal: false })
          successOrFailureToaster({ message, type: 'fail' })
        }
      )
    }
  }

  const confirmHandler = () => {
    setFlags({ spinner: true, disputeModal: false })
    const data = { company_invoice_id: pathState.invoice_id, company_invoice_item_id: pathState.invoice_item_id }
    InvoicesApi.confirm_invoice({ data },
      (_) => {
        setFlags({ spinner: false, disputeModal: false })
        fetchData()
        successOrFailureToaster({ message: 'Invoice has been confirmed' })
      },
      (err) => {
        const message = (err.response && err.response.data && err.message) || 'Error occurred while confirming invoice'
        setFlags({ spinner: false, disputeModal: false })
        successOrFailureToaster({ message, type: 'fail' })
      }
    )
  }

  const downloadHadnler = () => {
    setFlags({ spinner: true, disputeModal: false })
    const data = { company_invoice_id: pathState.invoice_id, company_invoice_item_id: pathState.invoice_item_id }
    InvoicesApi.download_participation_list({ data, responseType: 'blob' },
      (res) => {
        const url = window.URL.createObjectURL(new Blob([res.data], { type: res.headers['content-type'] }))
        const link = document.createElement('a')
        let fileName = 'participation list.csv'
        if (res.headers['content-type'] === 'application/pdf') {
          fileName = 'participation list.pdf'
        } else if (['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.ms-excel'].includes(res.headers['content-type'])) {
          fileName = 'participation list.xlsx'
        }
        link.href = url
        link.setAttribute('download', fileName)
        link.click()
        link.remove()

        setFlags({ spinner: false, disputeModal: false })
      },
      (err) => {
        const message = (err.response && err.response.data && err.response.data.msg) || 'Error occurred while downloading participation list'
        setFlags({ spinner: false, disputeModal: false })
        successOrFailureToaster({ message, type: 'fail' })
      }
    )
  }

  useEffect(() => {
    fetchData()
  }, [])

  const service = (pathState.service || '').trim().toLowerCase()
  const isInvoiceRejected = (data.invoice_status || '').trim().toLowerCase() === 'rejected'
  const isInvoiceConfirmed = ['confirmed', 'auto confirmed'].includes((data.invoice_status || '').trim().toLowerCase())
  const isStaff = (sessionStorage.getItem('role') || '').toLowerCase() === 'staff'
  const tableData = (data.appointments || data.customers || [])
  const listHeaders = getParticipationListHeaders(service)
  const hasTable = listHeaders.length > 0 && tableData.length > 0

  return (
    <Spinner visible={flags.spinner} >
      <div className='min-h-screen bg-gray-100 p-8'>
        <Breadcrumb list={[{ text: 'Invoices', link: '/invoices' }, { text: 'Participation list', link: '#' }]} classes='mb-1' />
        <div className='flex justify-between items-center'>
          <MainHeading mainHeading={location.state.service} />
          <img src={closebtn} alt='close-btn' className='cursor-pointer h-8 w-8' onClick={() => history.goBack()} />
        </div>

        <Divider classes='mt-5 mb-6' />

        {/* summary */}
        <div className={`bg-white border border-gray-300 rounded-ds-lg p-ds-6 ${!data.summary_loading ? '' : 'flex items-center justify-center h-44'}`}>
          {!data.summary_loading ?
            <React.Fragment>
              <div className='flex'>
                <table className='w-4/5 border-separate'>
                  <thead>
                    <tr>
                      {
                        getBreakUpHeaders(service, data.gst_percentage || 0).map(item => <th className='border-b border-gray-300 bg-gray-50 px-4 py-3 text-left text-2xs font-bold uppercase tracking-widest text-gray-600'>{item}</th>)
                      }
                    </tr>
                  </thead>
                  <tbody className='text-gray-900'>
                    {
                      (data['invoice_item_details'] || []).length > 0 ? (data['invoice_item_details'] || []).map(item_data => {
                        return (
                          invoiceBreakUpSchema(service, item_data)
                        )
                      }) : (
                        <tr>
                          <td colSpan={getBreakUpHeaders(service, data.gst_percentage || 0).length} className="px-4 py-8 text-center text-sm text-gray-500 font-fig-normal">
                            No results found
                          </td>
                        </tr>
                      )
                    }
                  </tbody>
                </table>
                <Divider type='vertical' classes='mt-2 border-gray-300' />
                <div className='w-1/5 flex flex-col items-center justify-center gap-ds-1'>
                  <span className='text-ts-xs font-semibold uppercase tracking-[0.1em] text-gray-500'>Amount to be paid</span>
                  <span className='font-jetbrains-mono text-ts-2xl font-semibold tabular-nums text-gray-900'>₹{data['total_amount'] || 0}</span>
                  {(isInvoiceConfirmed || isInvoiceRejected) && <span className={`mt-ds-1 rounded-ds-pill px-ds-3 py-1 text-ts-xs font-semibold ${isInvoiceConfirmed ? 'text-ds-green-dk bg-ds-green-lt' : 'text-ds-rose-dk bg-ds-rose-lt'}`}>{data.invoice_status}</span>}
                </div>
              </div>
              {!isInvoiceConfirmed && (isStaff || isInvoiceRejected) &&
                <React.Fragment>
                  <Divider classes='border-gray-300 my-6' />
                  <div className={`flex ${isInvoiceRejected ? 'flex-col' : 'justify-center items-center'}`}>
                    <p className='mb-0 text-gray-900 font-semibold'>{isInvoiceRejected ? 'Reason for rejection' : 'Please accept the invoice by 06th of every month or else invoice will be auto confirmed'}</p>
                    {isInvoiceRejected ?
                      <React.Fragment>
                        <p className='bg-white mb-0 mt-3 rounded-ds-md border border-gray-300 p-4 text-gray-900 font-semibold'>{data.comment}</p>
                      </React.Fragment>
                      :
                      <React.Fragment>
                        <button
                          onClick={confirmHandler}
                          disabled={SALES_ROLE === "sales"}
                          className={joinButtonClasses(dsButtonPrimary, 'ml-16 h-11 px-6', SALES_ROLE === 'sales' && 'cursor-not-allowed')}
                        >
                          <img src={white_tick_icon} alt='white-tick' className='inline mr-2' />
                          Confirm
                        </button>
                        <button
                          onClick={() => disputeHandler('open')}
                          disabled={SALES_ROLE === "sales"}
                          className={joinButtonClasses(dsButtonSecondary, 'ml-3 h-11 px-6', SALES_ROLE === 'sales' && 'cursor-not-allowed')}
                        >
                          <img src={x_red} alt='red-close' className='inline mr-2' />
                          Raise dispute
                        </button>
                      </React.Fragment>
                    }
                  </div>
                </React.Fragment>
              }
            </React.Fragment>
            :
            <SpinnerDotted />
          }
        </div>


        {/* filter */}
        <div className='bg-white border border-gray-300 rounded-ds-lg px-4 py-3 flex justify-between items-center mt-8'>
          <div className='flex items-center'>
            <span className='text-ts-xs font-semibold uppercase tracking-[0.1em] text-gray-500 mr-3'>From date</span><DatePicker classes='mr-6' value={dates.current.start_date} disabled={true} />
            <span className='text-ts-xs font-semibold uppercase tracking-[0.1em] text-gray-500 mr-3'>To date</span><DatePicker value={dates.current.end_date} disabled={true} />
          </div>
          <button
            onClick={downloadHadnler}
            disabled={SALES_ROLE === "sales"}
            className={joinButtonClasses(dsButtonGhostBrand, 'h-11 px-6', SALES_ROLE === 'sales' && 'cursor-not-allowed')}
          >
            <DSIcon name='download' size={18} className='mr-2' />
            Download participants list
          </button>
        </div>

        {/* participation list */}
        {listHeaders.length > 0 ? (
          <React.Fragment>
            <Table
              loading={data.table_loading}
              headers={listHeaders}
              tableschema={misParticipantsListSchema(service)}
              data={tableData}
              classes='mt-6 rounded-ds-lg'
              tableClasses='alternate_gray200_color'
            />
            {hasTable &&
              <Pagination
                totalpages={paginationData.total_pages}
                totalresults={paginationData.total_results}
                curpage={paginationData.cur_page}
                handler={navigateToPage}
              />
            }
          </React.Fragment>
        ) : (
          !data.table_loading &&
          <div className='mt-6 rounded-ds-lg border border-gray-300 bg-white px-4 py-12 text-center text-ts-sm text-gray-500'>
            No participants to display
          </div>
        )}
        <RaiseDisputeModal visible={flags.disputeModal} service={pathState.service || ''} bill_amount={data['total_amount'] || 0} close={() => disputeHandler('close')} submitHandler={(v) => disputeHandler('submit', v)} />
      </div>
    </Spinner>
  )
}

export default MISInvoices
